import { ReactNode, useEffect, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useBusyLabels } from '../../hooks/useBusy';
import './Loading.scss';

/** How long a request may take before the bar shows: a quick answer should not flash it. */
const SHOW_AFTER_MS = 250;

/**
 * The bar at the top of the screen while something someone waits for is loading, with what
 * it is. Fixed, so it is seen wherever the page is scrolled to.
 */
export const GlobalBusyBar = () => {
    const labels = useBusyLabels();
    const [shown, setShown] = useState<boolean>(false);
    const busy = labels.length > 0;

    useEffect(() => {
        if (!busy) {
            setShown(false);
            return;
        }

        const timer = window.setTimeout(() => setShown(true), SHOW_AFTER_MS);

        return () => window.clearTimeout(timer);
    }, [busy]);

    if (!busy || !shown) {
        return null;
    }

    const first = labels[0];
    const more = labels.length > 1 ? ` (+${labels.length - 1})` : '';

    return (
        <div className="busy-bar" role="status" aria-live="polite">
            <div className="busy-bar__track" aria-hidden="true">
                <span />
            </div>
            <div className="busy-bar__label">
                <FontAwesomeIcon icon="spinner" spin /> {first}
                {more}
            </div>
        </div>
    );
};

/**
 * The first load of a part of the page: a clear placeholder where the content will be,
 * instead of an empty table or a "nothing found" that is not true yet.
 */
export const LoadingBlock = ({ label, compact }: { label: string; compact?: boolean }) => (
    <div className={`loading-block${compact ? ' loading-block--compact' : ''}`} role="status" aria-live="polite">
        <FontAwesomeIcon icon="spinner" spin className="loading-block__spinner" />
        <span>{label}</span>
    </div>
);

/**
 * A part of the page that is loaded again: what was there stays, dimmed and not clickable,
 * with a line on top that says it is being brought up to date.
 */
export const Refreshing = ({ active, label, children }: { active: boolean; label: string; children: ReactNode }) => (
    <div className={`refreshing${active ? ' is-active' : ''}`} aria-busy={active}>
        {active && (
            <div className="refreshing__note" role="status" aria-live="polite">
                <FontAwesomeIcon icon="spinner" spin /> {label}
            </div>
        )}
        <div className="refreshing__content">{children}</div>
    </div>
);
